import { flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { MessageModel } from "../../components/common/message";
import { allOptionCode } from "../../components/project/documentobjective/ProjectTypeDropdownList";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { PaginationModel } from "../share/PagingModel";
import { ProjectModel,ProjectModelGetList } from "./ProjectModel";
import projectAPI from "./ProjectService";

export const ProjectListModel = types
    .model("ProjectListModel", {
        list: customtypes.optional(types.array(ProjectModelGetList), []),
        paging: customtypes.optional(PaginationModel, {
            pageSize: 10,
            sortBy: {
                columnName: "updated_date",
            },
        }),
        organizationID: types.maybeNull(types.number),
        projectType: types.maybeNull(types.string),
        newiipmFilter: types.maybeNull(types.string),
        startYear: types.maybeNull(types.number),
        error: customtypes.optional(ErrorModel, {}),
        loading: types.optional(types.boolean, false),
        alert: types.optional(MessageModel, {}),
        plan: types.optional(types.boolean, false),
        planId: types.maybe(types.number),
        IsCanceled: types.optional(types.number, 0), // Field to track archived status
        selectedItems: types.optional(types.array(types.number), []),
        source: types.maybeNull(types.string), // Field to track the source of the page
        dropdownOptions: types.optional(types.array(types.frozen()), []),
    })
    .views((self: any) => ({
        //
        get projectNameOptions() {
            return self.list.map((project: any) => ({
                text: project.projectTHName,
                value: project.projectID, // ใช้ projectID เพื่อความ unique
            }));
        }
    }))
    .actions((self: any) => ({
        debugLog(value: any, fieldName: string) {
            console.log(`Debugging field: ${fieldName}`, value);
            return value;
        },
        // Toggle selection of project items
        toggleSelectItem: (projectId: number) => {
            const index = self.selectedItems.indexOf(projectId);
            if (index === -1) {
                self.selectedItems.push(projectId);
            } else {
                self.selectedItems.splice(index, 1);
            }
        },
        // Clear selected items
        clearSelectedItems: () => {
            self.selectedItems.clear();
        },
        setFieldForProject: (projectId: number, fieldData: {fieldname: string, value: any}) => {
            // Find the project to update
            const project = self.projects.find((p: any) => p.projectID === projectId);
            if (project) {
                project.setField(fieldData);
            }
        },
        // Individual project update method - for reference
        updateProjects: flow(function*() {
            try {
                self.loading = true;
                
                // Create an array of promises for each project update
                const updatePromises = self.selectedItems.map((projectId: number) => {
                    const project = self.list.find((p: any) => p.projectID === projectId);
                    
                    if (project) {
                        // Create a payload with the necessary fields
                        const payload = {
                            projectID: project.projectID,
                            IsCanceled: self.IsCanceled === 1 ? 0 : 1, // Toggle the IsCanceled value
                            // Include other required fields from project if needed
                        };
                        
                        // Call the API to update the project
                        return projectAPI.updateProject(project.projectID, payload);
                    }
                    return Promise.resolve(); // Skip if project not found
                });
                    
                // Wait for all updates to complete
                yield Promise.all(updatePromises);
                
                // Clear selected items after successful updates
                self.selectedItems.clear();
                
                self.loading = false;
                return true;
            } catch (e) {
                self.loading = false;
                self.error.tigger = true;
                self.error.code = e.code;
                self.error.title = e.name;
                self.error.message = e.message || "ไม่สามารถอัปเดตโครงการได้";
                self.error.technicalStack = e.technicalStack;
                throw e;
            }
        }),
        // Batch update method for projects
        updateProjectsList: flow(function*() {
            try {
                self.loading = true;
                
                if (self.selectedItems.length === 0) {
                    throw new Error("กรุณาเลือกโครงการที่ต้องการดำเนินการ");
                }
                
                // Create a single payload with all selected project IDs
                // Toggle the IsCanceled value based on current view state
                const payload = {
                    projectIDs: self.selectedItems,
                    IsCanceled: self.IsCanceled === 1 ? 0 : 1 // Toggle between archived (1) and active (0)
                };
                
                // Call the API with the batch update
                yield projectAPI.updateProjectsBatch(payload);
                
                // After successful update, refresh the list
                yield self.getProjectList();
                
                // Clear selected items after successful updates
                self.selectedItems.clear();
                
                self.loading = false;
                return true;
            } catch (e) {
                self.loading = false;
                self.error.tigger = true;
                self.error.code = e.code;
                self.error.title = e.name;
                self.error.message = e.message || "ไม่สามารถอัปเดตโครงการได้";
                self.error.technicalStack = e.technicalStack;
                throw e;
            }
        }),
        loadProjectListForDropdown: flow(function*(searchText?: string) {
        try {
            self.loading = true;
            
            // เรียก API แต่ไม่ไปแทนที่ self.list
            const result = yield projectAPI.getProjectList({
                organizationID: self.organizationID,
                projectType: null, // เอาทั้งหมด
                startYear: null,
                newiipmFilter: null,
                paging: {
                    pageSize: 100, // เพิ่มจำนวนให้เยอะหน่อย
                    pageNo: 1,
                    searchText: searchText
                },
                plan: false,
                planId: null,
                IsCanceled: 0,
                source: self.source,
            });
            
            // สร้าง array แยกสำหรับ dropdown options
            let projectList = [];
            if (Array.isArray(result!.data)) {
                projectList = result.data;
            } else if (Array.isArray(result!.list)) {
                projectList = result.list;
            } else if (Array.isArray(result)) {
                projectList = result;
            }
            
            // เก็บไว้ในตัวแปรแยก
            self.dropdownOptions = projectList.map((project: any) => ({
                text: project.projectTHName,
                value: project.projectID,
                data: project // เก็บข้อมูลเต็มไว้ด้วย
            }));
            
        } catch (e) {
            console.log(e);
        } finally {
            self.loading = false;
        }
    }),

    // เพิ่ม field สำหรับเก็บ dropdown options
        // เพิ่ม comprehensive logging ใน getProjectList getProjectListOnlend
        getProjectList: flow(function*() {
            try {
                self.loading = true;
                const result = yield projectAPI.getProjectList({
                    organizationID: self.organizationID,
                    projectType: self.projectType === allOptionCode ? null : self.projectType,
                    startYear: self.startYear,
                    newiipmFilter: self.newiipmFilter,
                    paging: self.paging,
                    plan: self.plan,
                    planId: self.planId,
                    IsCanceled: self.IsCanceled,
                    source: self.source,
                });
                
                let projectList = [];
                
                if (Array.isArray(result!.data)) {
                    projectList = result.data;
                } else if (Array.isArray(result!.list)) {
                    projectList = result.list;
                } else if (Array.isArray(result)) {
                    projectList = result;
                }
                
                console.log("=== FINAL ASSIGNMENT ===");
                console.log("Project list length:", projectList.length);
                console.log("First project:", projectList[0]);
                
                // ⭐ แทนที่บรรทัด self.list = projectList; ด้วยวิธีนี้
                self.list.clear();
                projectList.forEach(project => {
                    self.list.push(project);
                });
                
                self.setPagination(result);
                
            } catch (e) {
                        console.error("เกิดข้อผิดพลาดในการโหลดข้อมูลโครงการ:", e.message);
                        self.error.tigger = true;
                        self.error.code = e.code;
                        self.error.title = e.name;
                        self.error.message = e.message;
                        self.error.technicalStack = e.technicalStack;
                        throw e;
            } finally {
                self.loading = false;
            }
        }),
        // getProjectListOnlend: flow(function*() {
        //     try {
        //         self.loading = true;
        //         const result = yield projectAPI.getProjectList({
        //             organizationID: self.organizationID,
        //             projectType: self.projectType === allOptionCode ? null : self.projectType,
        //         });
                
        //         let projectList = [];
                
        //         if (Array.isArray(result!.data)) {
        //             projectList = result.data;
        //         } else if (Array.isArray(result!.list)) {
        //             projectList = result.list;
        //         } else if (Array.isArray(result)) {
        //             projectList = result;
        //         }
                
        //         console.log("=== FINAL ASSIGNMENT ===");
        //         console.log("Project list length:", projectList.length);
        //         console.log("First project:", projectList[0]);
                
        //         // ⭐ แทนที่บรรทัด self.list = projectList; ด้วยวิธีนี้
        //         self.list.clear();
        //         projectList.forEach(project => {
        //             self.list.push(project);
        //         });
                
        //     } catch (e) {
        //                 console.error("เกิดข้อผิดพลาดในการโหลดข้อมูลโครงการ:", e.message);
        //                 self.error.tigger = true;
        //                 self.error.code = e.code;
        //                 self.error.title = e.name;
        //                 self.error.message = e.message;
        //                 self.error.technicalStack = e.technicalStack;
        //                 throw e;
        //     } finally {
        //         self.loading = false;
        //     }
        // }),
        removeProject: flow(function* (projID: number) {
            try {
              self.loading = true;
              const result = yield projectAPI.removeProject(projID);
              console.log("removeProject", result);
              // self.setAllField(result.data);
              self.alert.tigger = true;
              self.alert.title = "ลบโครงการ / แผนงาน เรียบร้อย";
              self.alert.message = "ลบโครงการ / แผนงาน เรียบร้อยแล้ว";
            } catch (e) {
              self.error.tigger = true;
              self.error.code = e.code;
              self.error.title = e.code;
              self.error.message = "ไม่สามารถลบโครงการได้ เนื่องจากโครงการถูกผูกกับแผนการก่อหนี้ใหม่แล้ว";
              self.error.technicalStack = e.technicalStack;
              throw e;
            } finally {
              self.loading = false;
            }
          }),

        loadProjectList: flow(function* (searchText?: string) {
        try {
            self.loading = true;
            self.orgTypeCode = "";
            self.LawTypeCode = "";
            self.FinancelTypeCode = "";
            if (searchText) {
            self.paging.searchText = searchText;
            }
            const result = yield projectAPI.getProjectList({
                organizationID: self.organizationID,
                projectType: self.projectType === allOptionCode ? null : self.projectType,
                startYear: self.startYear,
                newiipmFilter: self.newiipmFilter,
                paging: self.paging,
                plan: self.plan,
                planId: self.planId,
                IsCanceled: self.IsCanceled,
                source: self.source,
            });
            self.list = result.data;
        } catch (e) {
            console.log(e);
        } finally {
            self.loading = false;
        }
        }),
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
        onSearchTextFilter: (value: any) => {
            self.setDefaultPagination();
            self.paging.searchText = value;
            self.getProjectList();
        },
        setPagination: (result: any) => {
            self.paging.pageSize = result.pageSize;
            self.paging.pageNo = result.pageNo;
            self.paging.totalRow = result.totalRow;
        },
        onSelectedPage: (pageNo: number) => {
            self.paging.pageNo = pageNo;
            self.getProjectList();
        },
        setDefaultPagination: () => {
            self.paging.pageNo = 1;
        },
        onClearFilter: () => {
            self.organizationID = undefined;
            self.list = [];
            self.startYear = null;
            self.newiipmFilter = null;
            // Note: We're not clearing IsCanceled here so the filter view remains consistent
          },
        setProjectType:  (text: string, value: any) => {
            self.projectType = value;
        },
        resetAll: () => {
            self.list = [];
            self.paging = {
                pageSize: 10,
                sortBy: {
                    columnName: "updated_date",
                },
            };
        },
    }));

export type IProjectListModel = typeof ProjectListModel.Type;